import { inject, computed } from 'vue';
import { useTypeChecker } from './useTypeChecker';

export const useManagerActions = (
    props,
    isPrescriptionScanNameCopied,
    isCopying,
    isRemotePrintableClinic,
    testWorkQueueTypesList,
    isFailedWork,
    canPrintOnRemote,
    initialTestWorkQueueTypesList
  ) => {
  const { states, testWorkQueueTypes: testWorkQueueTypesConstants, localStates } = inject('constants');
  const { hasRemotePrintingType, hasAutomaticDesignType, hasUnchangedType } = useTypeChecker();

  const getDigitalActions = () => {
    const conditionalActions = []
    const baseActions = [
      { name: 'suspend', icon: 'iso-in/suspend.svg', label: 'Suspend', colorClass: 'dark-violet', disabled: false, action: states.ON_HOLD },
      { name: 'reject', icon: 'iso-in/refuses.svg', label: 'Fail', colorClass: 'red', disabled: false, action: states.ISO_FAILED },
      { name: 'changeTest', icon: 'iso-in/reassign.svg', label: 'Change test', colorClass: 'yellow', disabled: false, action: states.ISO_IN_CHANGED_TEST },
      { name: 'changeDate', icon: 'logistic-checkin/calendar.svg', label: 'Change date', colorClass: 'text-secondary', disabled: false, action: states.ISO_IN_CHANGED_DATE },
      { name: 'custom', icon: 'iso-in/custom.svg', label: 'Custom', colorClass: 'dark-gray', disabled: false, action: states.ISO_CUSTOM }
    ];

    if (isPrescriptionScanNameCopied.value && !isCopying.value) {
      conditionalActions.push({ name: 'approve', icon: 'manager/resume.svg', label: 'Approve', colorClass: 'green', disabled: false, action: states.COMPLETED })
      conditionalActions.push({ name: 'approveNoIso', icon: 'manager/resume-orange.svg', label: 'Approve without ISO', colorClass: 'orange', action: states.COMPLETED, payload: testWorkQueueTypesConstants.ISO_SKIPPED })

      if (isRemotePrintableClinic(props.prescription.clinic_id)) {
        const isPrinted = hasRemotePrintingType(testWorkQueueTypesList.value);
        conditionalActions.push({ name: 'chairSide', icon: isPrinted ? '3dPrinterSuccess.svg' : '3dPrinter.svg', label: isPrinted ? 'Remove print in clinic' : 'Report print in clinic', colorClass: 'violet', disabled: false, action: 'handleType', payload: testWorkQueueTypesConstants.REMOTE_PRINTING });
      }

      conditionalActions.push({ name: 'automaticDesign', icon: hasAutomaticDesignType(testWorkQueueTypesList.value) ? 'automaticDesignSelected.svg' : 'automaticDesign.svg', label: 'Automatic design', colorClass: 'violet', disabled: false, action: 'handleType', payload: testWorkQueueTypesConstants.AUTOMATIC_DESIGN })

    }

    if (!isPrescriptionScanNameCopied.value) {
      conditionalActions.push({ name: 'copyName', icon: 'copy.svg', label: 'Copy file name', colorClass: 'black', disabled: isCopying.value, action: 'copyName' },)
    }

    if (hasUnchangedType(initialTestWorkQueueTypesList.value)) {
      const isUnchangedActive = hasUnchangedType(testWorkQueueTypesList.value);
      conditionalActions.push({
        name: 'toggleUnchanged',
        icon: 'grade-a-plus.svg',
        label: isUnchangedActive ? 'Rimuovi perfetta' : 'Ripristina perfetta',
        colorClass: 'green',
        disabled: false,
        action: 'handleType',
        payload: testWorkQueueTypesConstants.UNCHANGED
      });
    }

    return [...conditionalActions, ...baseActions];
  };

  const getManagerActions = () => {
    const conditionalActions = [];
    const baseActions = [
      { action: states.MANAGER_SKIPPED, name: 'skip', icon: 'skip.svg', label: 'Skip', colorClass: 'dark-gray', disabled: false },
      { action: isFailedWork.value ? 'resume-failed' : 'resume', name: 'resume', icon: 'manager/resume.svg', label: 'Reasume', colorClass: 'green', disabled: false },
      { action: states.MANAGER_FAILED, name: 'fail', icon: 'manager/faild.svg', label: 'Failed', colorClass: 'red', disabled: isFailedWork.value },
      { action: 'change-technician', name: 'changeTechnician', icon: 'manager/reassign.svg', label: 'Change technician', colorClass: 'yellow', disabled: isFailedWork.value },
      { name: 'changePhase', icon: 'iso-in/changed-test.svg', label: 'Change internal phase', colorClass: 'text-secondary', disabled: isFailedWork.value, action: states.MANAGER_REASSIGNED },
      { name: 'changeTest', icon: 'manager/change-test.svg', label: 'Change test', colorClass: 'dark-violet', disabled: isFailedWork.value, action: states.MANAGER_CHANGED_TEST },
      { name: 'changeDate', icon: 'logistic-checkin/calendar.svg', label: 'Change date', colorClass: 'text-secondary', disabled: false, action: states.MANAGER_CHANGED_DATE },
      { name: 'custom', icon: 'iso-in/custom.svg', label: 'Custom', colorClass: 'dark-gray', disabled: false, action: localStates.MANAGER_CUSTOM }
    ];

    if (canPrintOnRemote.value) {
      conditionalActions.push({ name: 'print', icon: '3dPrinter.svg', label: 'Stampa in clinica', colorClass: 'dark-violet', disabled: isFailedWork.value, action: 'remotePrint' });
    }

    return [...conditionalActions, ...baseActions];
  };

  const getIsoActions = () => {
    const conditionalActions = [];
    const baseActions = [
      { name: 'reject', icon: 'iso-in/refuses.svg', label: 'Reject', colorClass: 'red', disabled: false, action: states.ISO_FAILED },
      { name: 'suspend', icon: 'iso-in/suspend.svg', label: 'Suspend', colorClass: 'dark-violet', disabled: false, action: states.ON_HOLD },
      { name: 'changeTest', icon: 'iso-in/reassign.svg', label: 'Change test', colorClass: 'yellow', disabled: !props.canChangeTest, action: states.ISO_IN_CHANGED_TEST },
      { name: 'changeDate', icon: 'logistic-checkin/calendar.svg', label: 'Change date', colorClass: 'text-secondary', disabled: false, action: states.ISO_IN_CHANGED_DATE },
      { name: 'custom', icon: 'iso-in/custom.svg', label: 'Custom', colorClass: 'dark-gray', disabled: false, action: states.ISO_CUSTOM }
    ];

    if (!props.hasChildren && !props.hasParent) {
      conditionalActions.push({ name: 'chooseFork', icon: 'iso-in/fork-right.svg', label: 'Choose fork', colorClass: 'dark-gray', disabled: false, action: states.ISO_IN_CHOOSE_FORK });
    }

    if (props.hasChildren || props.hasParent) {
      conditionalActions.push({ name: 'accept', icon: 'iso-in/accept.svg', label: 'Accept', colorClass: 'green', disabled: false, action: states.COMPLETED });
      conditionalActions.push({ name: 'reassign', icon: 'iso-in/changed-test.svg', label: 'Reassign', colorClass: 'dark-gray', disabled: false, action: states.ISO_REASSIGNED });
    }

    if ((props.hasChildren || props.hasParent) && props.isMobileDepartment) {
      conditionalActions.push({ name: 'external', icon: 'van.svg', label: 'External phase', colorClass: 'violet', disabled: false, action: 'external' });
    }

    conditionalActions.push({ name: 'automaticDesign', icon: hasAutomaticDesignType(testWorkQueueTypesList.value) ? 'automaticDesignSelected.svg' : 'automaticDesign.svg', label: 'Automatic design', colorClass: 'violet', disabled: false, action: 'handleType', payload: testWorkQueueTypesConstants.AUTOMATIC_DESIGN })

    return [...conditionalActions, ...baseActions];
  };

  const getActionsByMode = (mode) => {
    return computed(() => {
      testWorkQueueTypesList.value;
      initialTestWorkQueueTypesList.value;
      switch (mode) {
        case 'digitalCheckIn':
          return getDigitalActions();
        case 'manager':
          return getManagerActions();
        case 'isoDepartment':
          return getIsoActions();
        default:
          return [];
      }
    });
  };


  return { getActionsByMode };
};
